import { clearAuthToken, getAuthToken } from '../lib/auth/token';
import type { InviteeImportPreview, InviteeImportResult } from '../lib/inviteeImport';
import axios, { AxiosInstance } from 'axios';
import type {
  AbandonReminderPlanResult,
  GroupDetail,
  GroupInviteeInput,
  GroupBroadcastSummary,
  GroupItineraryItem,
  GroupPayload,
  GroupRoomRequest,
  GroupSummary,
  AddRoomOccupantResponse,
  AssignReservationRequest,
  CreateDinnerHoldRequest,
  CreateRoomOccupantGuestRequest,
  DinnerClaimResult,
  GroupDinnerHold,
  HostClaimDinnerSpotRequest,
  HostReleaseDinnerClaimRequest,
  MoveDinnerClaimRequest,
  UpdateDinnerClaimRequest,
  UpdateDinnerHoldRequest,
  UpdateDinnerHoldResponse,
  GroupRoomOccupant,
  GroupScheduledBroadcast,
  LinkRoomOccupantRequest,
  RoomOccupantsResponse,
  RoomRequestStatusExtras,
  CreateReminderPlanBody,
  ReminderPlanResult,
  ReplaceReminderPlanBody,
  ReplaceReminderPlanResult,
  ScheduleGroupBroadcastRequest,
  ScheduledSequenceCancelResult,
  SendGroupBroadcastRequest,
  SmsEstimateRequest,
  SmsEstimateResponse,
  SendGroupBroadcastResponse,
  SendGroupInvitesRequest,
  SendGroupInvitesResponse,
  SendRoomConfirmationsRequest,
  SendRoomConfirmationsResponse,
} from '../types/groups';

const DEV_ORCHESTRATOR_API_URL = 'https://orchestrator.api.dev.tripsby.ai';
const envOrchestratorUrl = import.meta.env.VITE_ORCHESTRATOR_API_URL;
if (import.meta.env.PROD && !envOrchestratorUrl) {
  // Don't silently point a production build at the DEV orchestrator — make the
  // misconfiguration loud in prod logs so the build env gets fixed.
  // eslint-disable-next-line no-console
  console.error(
    'VITE_ORCHESTRATOR_API_URL is not set in this production build; falling back ' +
      'to the DEV orchestrator. Set it in the prod build environment.',
  );
}
export const ORCHESTRATOR_API_URL = envOrchestratorUrl || DEV_ORCHESTRATOR_API_URL;

export const GROUPS_PUBLIC_BASE_URL = (
  import.meta.env.VITE_GROUPS_PUBLIC_BASE_URL ||
  'https://tripsby.ai'
).replace(/\/$/, '');

export function groupPublicUrl(slug: string, inviteToken?: string) {
  const url = new URL(`/g/${slug}`, GROUPS_PUBLIC_BASE_URL);
  if (inviteToken) url.searchParams.set('invite', inviteToken);
  return url.toString();
}

let authToken: string | null = null;

const client: AxiosInstance = axios.create({
  baseURL: ORCHESTRATOR_API_URL.replace(/\/$/, ''),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  /*
    ProtectedRoute injects the token from an effect, which runs AFTER its
    children's mount effects — so on a hard load of a deep route the first
    requests can go out before setAuthToken lands. Fall back to storage, which
    is where that token came from anyway.
  */
  const bearer = authToken || getAuthToken();
  if (bearer) config.headers.Authorization = `Bearer ${bearer}`;
  return config;
});

/*
  The recovery path. Expiry arithmetic can drift from what the server enforces
  for all sorts of reasons — clock skew, a token revoked early, a backend that
  shortens lifetimes — and when it does, the app used to sit on a spinner
  forever: it believed it was signed in, every request 401'd, and nothing
  reconciled the two. There is also no sign-out in the UI, so a host had no way
  out at all short of clearing site data.

  A 401 is the server's authoritative answer, so treat it as the truth: drop the
  local session and send them to sign in again.
*/
function handleUnauthorized() {
  try {
    clearAuthToken()
  } catch {
    /* storage unavailable — redirect anyway, the server already refused us */
  }
  if (typeof window !== 'undefined' && !/\/login/.test(window.location.pathname)) {
    window.location.href = '/login'
  }
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) handleUnauthorized()
    return Promise.reject(error)
  },
)

/*
  Exported so other orchestrator clients (the guest tracker) reuse this one
  instance rather than standing up a competing stack: one bearer, and one 401
  recovery path above.
*/
export const orchestratorClient = client;

export const groupsApi = {
  setAuthToken(token: string) {
    authToken = token;
  },
  clearAuthToken() {
    authToken = null;
  },
  async listGroups(): Promise<GroupSummary[]> {
    const { data } = await client.get('/api/groups');
    return data;
  },
  async createGroup(body: GroupPayload): Promise<GroupDetail> {
    const { data } = await client.post('/api/groups', body);
    return data;
  },
  async getGroup(id: string): Promise<GroupDetail> {
    const { data } = await client.get(`/api/groups/${id}`);
    return data;
  },
  async updateGroup(id: string, body: GroupPayload): Promise<GroupDetail> {
    const { data } = await client.patch(`/api/groups/${id}`, body);
    return data;
  },
  /**
   * Returns the saved tile as the server stored it. Callers that need to know
   * what actually changed — see lib/changeNotice.ts — read the resolved value
   * of this promise rather than re-reading `group.itineraryItems`, which is
   * only refreshed by the caller's own `onChanged` refetch.
   */
  async upsertItineraryItem(groupId: string, body: Record<string, unknown>): Promise<GroupItineraryItem> {
    const { data } = await client.post(`/api/groups/${groupId}/itinerary-items`, body);
    return data;
  },
  async createRoomBlock(groupId: string, body: Record<string, unknown>) {
    const { data } = await client.post(`/api/groups/${groupId}/room-blocks`, body);
    return data;
  },
  /**
   * What this import would do, computed by the server's own matcher — the same
   * one its write runs. Studio does not model the matching rule; that is what
   * let the preview promise one guest and the write update another.
   */
  async previewInviteeImport(
    groupId: string,
    invitees: GroupInviteeInput[],
  ): Promise<InviteeImportPreview> {
    const { data } = await client.post(`/api/groups/${groupId}/invitees/import/preview`, { invitees });
    return data;
  },
  /**
   * Write the import. `rosterFingerprint` is the one returned by the preview the
   * host approved: it makes the server resolve on the accepted invitee ids alone,
   * and makes it refuse (409) if the roster moved while they were reviewing.
   */
  async importInvitees(
    groupId: string,
    invitees: GroupInviteeInput[],
    rosterFingerprint?: string,
  ): Promise<InviteeImportResult> {
    const { data } = await client.post(
      `/api/groups/${groupId}/invitees/import`,
      { invitees, rosterFingerprint },
    );
    return data;
  },
  async sendInvites(groupId: string, body: SendGroupInvitesRequest): Promise<SendGroupInvitesResponse> {
    const { data } = await client.post(`/api/groups/${groupId}/invitees/send`, body);
    return data;
  },
  async sendBroadcast(groupId: string, body: SendGroupBroadcastRequest): Promise<SendGroupBroadcastResponse> {
    const { data } = await client.post(`/api/groups/${groupId}/broadcasts`, body);
    return data;
  },
  /** What a text would cost, composed by the server exactly as a send composes it. 404/405/501 on an API without it. */
  async estimateSms(groupId: string, body: SmsEstimateRequest): Promise<SmsEstimateResponse> {
    const { data } = await client.post(`/api/groups/${groupId}/sms-estimate`, body);
    return data;
  },
  async listBroadcasts(groupId: string): Promise<GroupBroadcastSummary[]> {
    const { data } = await client.get(`/api/groups/${groupId}/broadcasts`);
    return data;
  },
  /**
   * Schedule a broadcast for a future `sendAt`. The API runs its own dry-run
   * preview against today's audience and 400s the same way an interactive send
   * does; on an environment where migration 014 has not run, every scheduled-
   * broadcast endpoint answers 503 `{ code: "scheduled_broadcasts_unavailable" }`
   * instead of 500 — see `src/lib/scheduledBroadcasts.ts`.
   */
  async scheduleBroadcast(
    groupId: string,
    body: ScheduleGroupBroadcastRequest,
  ): Promise<GroupScheduledBroadcast> {
    const { data } = await client.post(`/api/groups/${groupId}/scheduled-broadcasts`, body);
    return data;
  },
  /**
   * A WHOLE reminder plan, created atomically (OrchestratorAPI #401): 201 `{ alreadyExisted: false, rows }`, or 200
   * `{ alreadyExisted: true, rows }` for a repeat of the same plan (a retry after a timeout is always safe). 400 names
   * the failing step (`steps[i]`) and writes nothing; 409 `plan_conflict` / `sequence_cancelled`.
   */
  async createReminderPlan(groupId: string, body: CreateReminderPlanBody): Promise<ReminderPlanResult> {
    const { data } = await client.post(`/api/groups/${groupId}/scheduled-broadcasts/plans`, body);
    return data;
  },
  /**
   * Reschedule as ONE call: cancels the old plan's pending steps and creates the new plan in a single transaction.
   * 409 `nothing_to_reschedule` when the old plan has nothing pending (already rescheduled, sent or cancelled).
   */
  async replaceReminderPlan(
    groupId: string,
    oldSequenceId: string,
    body: ReplaceReminderPlanBody,
  ): Promise<ReplaceReminderPlanResult> {
    const { data } = await client.post(`/api/groups/${groupId}/scheduled-broadcasts/plans/${oldSequenceId}/replace`, body);
    return data;
  },
  /**
   * "Discard" a plan create whose outcome was never learned. The server waits for any in-flight create of that id, then either reports
   * its rows (`abandoned: false`: it was saved after all) or records a tombstone so a create that commits late is refused
   * (`abandoned: true`). Only a definitive answer settles the attempt.
   */
  async abandonReminderPlan(groupId: string, sequenceId: string): Promise<AbandonReminderPlanResult> {
    const { data } = await client.post(`/api/groups/${groupId}/scheduled-broadcasts/plans/${sequenceId}/abandon`);
    return data;
  },
  async listScheduledBroadcasts(groupId: string): Promise<GroupScheduledBroadcast[]> {
    const { data } = await client.get(`/api/groups/${groupId}/scheduled-broadcasts`);
    return data;
  },
  /**
   * 409 when the row already fired/failed/was cancelled — the body carries the
   * row's current state as `broadcast`, which `scheduledBroadcasts.ts` reads
   * instead of a generic error.
   */
  async cancelScheduledBroadcast(groupId: string, id: string): Promise<GroupScheduledBroadcast> {
    const { data } = await client.delete(`/api/groups/${groupId}/scheduled-broadcasts/${id}`);
    return data;
  },
  /**
   * Cancels every still-pending row of one reminder plan (OrchestratorAPI #394).
   * 404 for an unknown plan, 503 `reminder_sequences_unavailable` before
   * migration 016 has run.
   */
  async cancelScheduledBroadcastSequence(
    groupId: string,
    sequenceId: string,
  ): Promise<ScheduledSequenceCancelResult> {
    const { data } = await client.delete(
      `/api/groups/${groupId}/scheduled-broadcasts/sequences/${sequenceId}`,
    );
    return data;
  },
  async assignReservation(groupId: string, body: AssignReservationRequest) {
    const { data } = await client.post(`/api/groups/${groupId}/room-requests`, body);
    return data;
  },
  async updateRoomRequestStatus(
    groupId: string,
    requestId: string,
    status: string,
    extras: RoomRequestStatusExtras = {},
  ) {
    const { data } = await client.patch(`/api/groups/${groupId}/room-requests/${requestId}`, {
      status,
      ...extras,
    });
    return data;
  },
  async sendRoomConfirmation(groupId: string, requestId: string) {
    const { data } = await client.post(`/api/groups/${groupId}/room-requests/${requestId}/send-confirmation`, {});
    return data as GroupRoomRequest;
  },
  async sendRoomConfirmations(groupId: string, body: SendRoomConfirmationsRequest): Promise<SendRoomConfirmationsResponse> {
    const { data } = await client.post(`/api/groups/${groupId}/room-requests/send-confirmations`, body);
    return data;
  },
  /**
   * Who is sleeping in this reservation. Host tier, so it carries contact
   * details; the guest-facing trip page only ever gets names.
   */
  async listRoomOccupants(groupId: string, requestId: string): Promise<RoomOccupantsResponse> {
    const { data } = await client.get(`/api/groups/${groupId}/room-requests/${requestId}/occupants`);
    return data;
  },
  /** Link a guest who is already on the trip's roster. */
  async linkRoomOccupant(
    groupId: string,
    requestId: string,
    body: LinkRoomOccupantRequest,
  ): Promise<AddRoomOccupantResponse> {
    const { data } = await client.post(`/api/groups/${groupId}/room-requests/${requestId}/occupants`, body);
    return data;
  },
  /**
   * Create a guest and put them in the room in one write. The API matches on
   * email first and links the existing invitee when it hits — `created: false`
   * in the response. Studio must not pre-empt that with its own matching rule:
   * two rules that disagree is how a 137-person roster became 274.
   */
  async createRoomOccupantGuest(
    groupId: string,
    requestId: string,
    body: CreateRoomOccupantGuestRequest,
  ): Promise<AddRoomOccupantResponse> {
    const { data } = await client.post(`/api/groups/${groupId}/room-requests/${requestId}/occupants/guest`, body);
    return data;
  },
  /**
   * Move the primary. There is no demote — the API rejects `isPrimary: false`,
   * because a room with occupants and no primary quotes its rate to nobody.
   */
  async setRoomOccupantPrimary(
    groupId: string,
    requestId: string,
    occupantId: string,
  ): Promise<{ occupants: GroupRoomOccupant[] }> {
    const { data } = await client.patch(
      `/api/groups/${groupId}/room-requests/${requestId}/occupants/${occupantId}`,
      { isPrimary: true },
    );
    return data;
  },
  /** Take someone out of the room. Deliberately does NOT shrink party_size. */
  async removeRoomOccupant(groupId: string, requestId: string, occupantId: string): Promise<void> {
    await client.delete(`/api/groups/${groupId}/room-requests/${requestId}/occupants/${occupantId}`);
  },
  async markManualInvoice(groupId: string, requestId: string, body: Record<string, unknown>) {
    const { data } = await client.post(`/api/groups/${groupId}/room-requests/${requestId}/manual-invoice`, body);
    return data;
  },
  async listDinnerHolds(groupId: string): Promise<GroupDinnerHold[]> {
    const { data } = await client.get(`/api/groups/${groupId}/dinner-holds`);
    return data;
  },
  async createDinnerHold(groupId: string, body: CreateDinnerHoldRequest): Promise<GroupDinnerHold> {
    const { data } = await client.post(`/api/groups/${groupId}/dinner-holds`, body);
    return data;
  },
  /**
   * Retires an itinerary item. Not a delete: guests have seen it on their
   * timeline, so it stays as history and the reason is shown to them.
   */
  async cancelItineraryItem(groupId: string, itemId: string, reason: string): Promise<GroupItineraryItem> {
    const { data } = await client.post(
      `/api/groups/${groupId}/itinerary-items/${itemId}/cancel`,
      { reason },
    );
    return data;
  },
  /**
   * Edits a dinner in place — the alternative to delete-and-recreate, which
   * cancels every guest's claim. The ONLY call site for this route.
   *
   * Body from `buildDinnerPatch` (lib/dinnerEdit.ts). Refusals are 409s whose
   * `code` is `capacity_below_claimed`, `hold_cancelled` or `stale`; read them
   * with `readDinnerEditError`. This sends nobody anything — the notice is a
   * separate, host-confirmed broadcast.
   */
  async updateDinnerHold(
    groupId: string,
    holdId: string,
    body: UpdateDinnerHoldRequest,
  ): Promise<UpdateDinnerHoldResponse> {
    const { data } = await client.patch(`/api/groups/${groupId}/dinner-holds/${holdId}`, body);
    return data;
  },
  async deleteDinnerHold(groupId: string, holdId: string): Promise<void> {
    await client.delete(`/api/groups/${groupId}/dinner-holds/${holdId}`);
  },
  /**
   * Save a seat at a table for a guest who cannot save it themselves — the guests
   * with neither an email nor a phone on file, who can never sign in and can
   * never be sent a link, so no guest-facing endpoint reaches them at all.
   *
   * Host-authenticated and owner-scoped, which is the only reason it may take an
   * invitee id: the id came off the caller's own roster. The server re-reads that
   * invitee scoped to the group it locked, so a stale or foreign id is a 404
   * rather than a write into somebody else's trip.
   *
   * Every refusal comes back as a `DinnerClaimResult`, including the 4xx ones —
   * 409 `dinner_conflict` naming the other venue, 400 `party_too_large` carrying
   * the cap. Read them with `lib/dinnerClaim.ts`; `mutationErrorMessage` finds no
   * key it recognises in that body and degrades to "Request failed with status
   * code 409".
   */
  async claimDinnerSpotForInvitee(
    groupId: string,
    holdId: string,
    body: HostClaimDinnerSpotRequest,
  ): Promise<DinnerClaimResult> {
    const { data } = await client.post(
      `/api/groups/${groupId}/dinner-holds/${holdId}/claims`,
      body,
    );
    return data;
  },
  /**
   * Take one named person off one table.
   *
   * POST rather than DELETE on the claim, for two reasons that both bite in a
   * restaurant: it matches the verb and the `{ reason }` body of the two
   * guest-tier release routes, and a DELETE-with-body is dropped by enough
   * proxies that the reason would have to move into a query string, where
   * free text does not belong.
   *
   * Nothing is hard-deleted. The row keeps its id, its `claimedAt` and its
   * invitee, and comes back under `formerClaims` — released, never cancelled,
   * so the audit list can still tell "the dinner was called off" from "the host
   * took Ana off this table".
   *
   * Refusals arrive as `DinnerClaimResult`, like every other claim route. Read
   * them with `lib/dinnerClaimEdit.ts` — the EDIT copy, not `lib/dinnerClaim.ts`,
   * which words the same statuses for the ADD path.
   */
  async releaseDinnerClaim(
    groupId: string,
    holdId: string,
    claimId: string,
    body: HostReleaseDinnerClaimRequest = {},
  ): Promise<DinnerClaimResult> {
    const { data } = await client.post(
      `/api/groups/${groupId}/dinner-holds/${holdId}/claims/${claimId}/release`,
      body,
    );
    return data;
  },
  /**
   * Rename the person in a seat, resize their party, or both.
   *
   * Send only what is changing: the server reads an absent field as "leave it
   * alone" and an explicit null as an error, so a client that helpfully fills in
   * the unchanged half is asking for a 400.
   *
   * A rename is refused with 409 `linked_to_invitee` when the claim is tied to a
   * roster row, and refused BEFORE anything is written — a combined name+size
   * patch that hits it changes neither. The refusal is not a bug to route
   * around: the seating chart already renders the roster name over the claim
   * name, so an allowed rename would change the dinner list and silently not
   * change the seating chart. Read it with `lib/dinnerClaimEdit.ts`.
   */
  async updateDinnerClaim(
    groupId: string,
    holdId: string,
    claimId: string,
    body: UpdateDinnerClaimRequest,
  ): Promise<DinnerClaimResult> {
    const { data } = await client.patch(
      `/api/groups/${groupId}/dinner-holds/${holdId}/claims/${claimId}`,
      body,
    );
    return data;
  },
  /**
   * Move one person to a different table.
   *
   * The `holdId` in the path is the SOURCE — that is what the server's ownership
   * chain anchors on, and the destination is resolved through the route's group
   * so a hold id from another trip matches nothing.
   *
   * Not release-then-claim, and Studio must never emulate it that way. Release +
   * claim leaves a `released` row on the source table that reads as "this guest
   * dropped out" when they did not, mints a new claim id and `claimedAt`, and a
   * failure between the two calls leaves a guest standing in a restaurant with
   * no seat at all. One `UPDATE` makes "exactly one row across the two tables"
   * structural.
   *
   * Refusals read with `lib/dinnerClaimEdit.ts`, which is also the only place
   * that knows a move refusal has to say "nothing moved" out loud.
   */
  async moveDinnerClaim(
    groupId: string,
    holdId: string,
    claimId: string,
    body: MoveDinnerClaimRequest,
  ): Promise<DinnerClaimResult> {
    const { data } = await client.post(
      `/api/groups/${groupId}/dinner-holds/${holdId}/claims/${claimId}/move`,
      body,
    );
    return data;
  },
};
